import React, { useMemo } from 'react';
import { Clock } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { SocialIcon } from '../icons/SocialIcon';
import { MediaDisplay } from '../posts/previews/MediaDisplay';
import { PdfThumb } from '../common/PdfThumb';
import { normalizeMedia } from '../../utils/mediaUtils';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Post } from '../../types/calendar';
import { useTranslation } from 'react-i18next';
import { getMediaTagVariant, tagStyles, tagEmoji, getTagLabels, normalizeBackendMediaType } from '../../utils/tagStyles';

interface PostPreviewProps {
  post: Post;
}

// Helper function to get platforms as array. usePosts normalizes the
// backend object format into a string[]; older call sites still pass the
// raw object. Accept both.
const getPlatformsArray = (post: any): string[] => {
  if (!post.platforms) return [];
  if (Array.isArray(post.platforms)) return post.platforms.filter((p: any): p is string => typeof p === 'string');
  if (typeof post.platforms === 'object') return Object.keys(post.platforms);
  return [];
};

export const PostPreview: React.FC<PostPreviewProps> = ({ post }) => {
  const { t } = useTranslation();
  const tagLabel = getTagLabels(t);
  const renderLinkedText = (text: string, colorHex = '#0B65C2') => {
    const elements: React.ReactNode[] = [];
    const lines = (text || '').split('\n');
    const regex = /(https?:\/\/[^\s]+|www\.[^\s]+|#[\p{L}\d_]+)/giu;
    lines.forEach((line, lineIdx) => {
      if (lineIdx > 0) elements.push(<br key={`br-${lineIdx}`} />);
      let lastIndex = 0;
      let match: RegExpExecArray | null;
      while ((match = regex.exec(line)) !== null) {
        const start = match.index;
        if (start > lastIndex) {
          elements.push(line.slice(lastIndex, start));
        }
        const token = match[0];
        if (token.startsWith('#')) {
          elements.push(
            <span key={`hash-${lineIdx}-${start}`} style={{ color: colorHex }}>{token}</span>
          );
        } else {
          const href = token.startsWith('http') ? token : `http://${token}`;
          elements.push(
            <a key={`link-${lineIdx}-${start}`} href={href} target="_blank" rel="noreferrer" className="hover:underline" style={{ color: colorHex }}>
              {token}
            </a>
          );
        }
        lastIndex = regex.lastIndex;
      }
      if (lastIndex < line.length) {
        elements.push(line.slice(lastIndex));
      }
    });
    return elements;
  };
  
  // Get platforms array using helper function
  const platformsArray = useMemo(() => getPlatformsArray(post), [post.platforms]);

  const getStatusBadge = (status: Post['status']) => {
    switch (status) {
      case 'draft':
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">📝 Draft</span>;
      case 'scheduled':
        return <span className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-full">📅 Scheduled</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded-full">⏳ Pending</span>;
      case 'ready_to_publish':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">🚀 Ready</span>;
      case 'published':
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded-full">✅ Published</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-full">❌ Rejected</span>;
      default:
        return <span className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">{status}</span>;
    }
  };

  const getFormattedTime = () => {
    // Try scheduledAt first, then fallback to createdAt
    const dateString = post.scheduledAt || post.createdAt;
    
    if (dateString) {
              try {
          const date = parseISO(dateString);
          if (isValid(date)) {
            return format(date, 'HH:mm');
          }
        } catch {
        // Fallback to new Date if parseISO fails
        try {
          const date = new Date(dateString);
          if (isValid(date)) {
            return format(date, 'HH:mm');
          }
        } catch {
          return '';
        }
      }
    }
    return '';
  };

  // Deprecated helpers kept previously; removed after unified tag styles


  return (
    <div className="w-full sm:w-[640px] md:w-[700px] lg:w-[800px] max-w-full sm:max-w-4xl bg-white border border-black rounded-lg shadow-lg p-4 z-50 overflow-hidden">
      {/* Header with brand, time and status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {post.brandLogo && (
            <img 
              src={post.brandLogo} 
              alt={post.brandName || 'Brand logo'}
              className="w-9 h-9 rounded-full object-cover flex-shrink-0"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="flex flex-col">
            {post.brandName && (
              <span className="text-sm font-medium text-gray-800">{post.brandName}</span>
            )}
            <div className="flex items-center gap-1 text-xs text-gray-600">
              <Clock className="w-3 h-3" />
              <span>{getFormattedTime() || 'No time'}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Platforms */}
          <div className="flex gap-2">
            {platformsArray.map((platform) => (
              <SocialIcon 
                key={platform}
                platform={platform as SocialPlatform}
                size={20}
              />
            ))}
          </div>
          {getStatusBadge(post.status)}
        </div>
      </div>

      {/* Main content area - horizontal layout */}
      <div className="flex flex-col md:flex-row gap-4 mb-3">
        {/* Media section */}
        <div className="flex-shrink-0">
          {(() => {
            const normalizedMedia = normalizeMedia(post.media);
            if (!normalizedMedia) return null;
            const isPdf = /\.pdf(\?|#|$)/i.test(normalizedMedia.url);
            return (
              <div className="w-full md:w-64 h-64 rounded-lg overflow-hidden bg-gray-100 mb-3">
                {isPdf ? (
                  <PdfThumb src={normalizedMedia.url} className="w-full h-full" frame="portrait4x5" />
                ) : (
                  <MediaDisplay
                    mediaUrl={normalizedMedia.url}
                    altText="Post media"
                    type={normalizedMedia.type}
                    className="w-full h-full"
                    objectFit="cover"
                    controls={false}
                    autoPlayOnMount={normalizedMedia.type === 'video'}
                    muted
                  />
                )}
              </div>
            );
          })()}
          
          {/* Media Format and Post Format tags - below image */}
          <div className="flex flex-col gap-2">
            <div className="flex gap-2">
              {(() => {
                const backend = normalizeBackendMediaType((post as any).postMediaType);
                const m = normalizeMedia(post.media);
                const v = backend ?? getMediaTagVariant(m?.url, m?.type);
                const s = tagStyles[v];
                return (
                  <div className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 border ${s.bg} ${s.text} ${s.border}`}>
                    <span>{tagEmoji[v]}</span>
                    <span>{tagLabel[v]}</span>
                  </div>
                );
              })()}
              {(() => {
                const fv = (post.format === 'reel' ? 'reel' : 'regular') as 'reel' | 'regular';
                const fs = tagStyles[fv];
                return (
                  <div className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 border ${fs.bg} ${fs.text} ${fs.border}`}>
                    <span>{tagEmoji[fv]}</span>
                    <span>{tagLabel[fv]}</span>
                  </div>
                );
              })()}
            </div>

            {/* Engagement Stats */}
            {post.status === 'published' && (post.engagement || ((post as any).platformResults && Object.keys((post as any).platformResults).length > 0)) && (
              <div className="flex flex-col gap-1 mt-2">
                <div className="text-xs font-medium text-gray-700 mb-1">📊 Engagement Stats</div>
                
                {/* Total Stats */}
                {post.engagement && (
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50">
                    <div className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1">
                        <span className="text-lg">📈</span>
                        <span className="font-medium text-gray-700">Total</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                          <span className="text-red-500">❤️</span>
                          <span>{post.engagement.likes}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-blue-500">💬</span>
                          <span>{post.engagement.comments}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-green-500">🔗</span>
                          <span>{post.engagement.shares}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-purple-500">👁️</span>
                          <span>{post.engagement.views}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>👉</span>
                          <span>{(post.engagement as any)?.clicks || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span>🔍</span>
                          <span>{(post.engagement as any)?.unique_views || (post.engagement as any)?.uniqueViews || (post.engagement as any)?.uniqueImpressions || 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                  {/* Platform-specific Stats */}
                  {(() => {
                    // Get all platforms with engagement data (even if all values are 0)
                    // First try to get from platformResults, platformData, or original platforms object
                    let platformsData = (post as any).platformResults || (post as any).platformData || {};
                    
                    // Fallback: if platforms is an object (not array), use it directly
                    if (Object.keys(platformsData).length === 0 && post.platforms && typeof post.platforms === 'object' && !Array.isArray(post.platforms)) {
                      platformsData = post.platforms;
                    }
                    
                    const platformsWithEngagement = Object.entries(platformsData)
                      .filter(([_, platformInfo]: [string, any]) => {
                        const engagement = platformInfo?.engagement || platformInfo?.results?.engagement;
                        return engagement !== undefined;
                      });
                    
                    // Show platform-specific stats if we have platforms with engagement
                    return platformsWithEngagement.length > 0 && 
                      platformsWithEngagement.map(([platformKey, platformInfo]: [string, any]) => {
                        const engagement = platformInfo?.engagement || platformInfo?.results?.engagement;
                        return (
                          <div key={platformKey} className="border border-gray-200 rounded-lg p-2 bg-white">
                              <div className="flex items-center justify-between text-xs">
                              <div className="flex items-center gap-1">
                                <SocialIcon platform={platformKey as SocialPlatform} size={12} />
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex items-center gap-1">
                                  <span className="text-red-500">❤️</span>
                                  <span>{engagement?.likes || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-blue-500">💬</span>
                                  <span>{engagement?.comments || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-green-500">🔗</span>
                                  <span>{engagement?.shares || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="text-purple-500">👁️</span>
                                  <span>{engagement?.views || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>👉</span>
                                  <span>{(engagement as any)?.clicks || 0}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <span>🔍</span>
                                  <span>{engagement?.unique_views || engagement?.uniqueViews || engagement?.uniqueImpressions || 0}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      });
                  })()}
              </div>
            )}
          </div>
        </div>

        {/* Content section */}
        <div className="flex-1 flex flex-col">
          {/* Content */}
          <div className="flex-1">
            <div className="text-sm text-gray-800 leading-relaxed break-words overflow-wrap-anywhere" style={{ whiteSpace: 'pre-wrap' }}>
              {renderLinkedText(post.content)}
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}; 